import { Box, TableCell } from "@mui/material";
import { DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { useUrlProvider } from "../../../useUrlProvider";
import { ActivityFieldType, ActivityFieldValue } from "../ActivitiesSearch.types";
import AnnualWheelActivityStatusChip from "../../annualWheelActivityStatusChip/AnnualWheelActivityStatusChip";
import AnnualWheelPriorityIcon from "../../annualWheelPriorityIcon/AnnualWheelPriorityIcon";
import { useRecurringIntervalFormatting } from "../../AnnualWheel.hooks";
import { ApprovalOption, RecurringIntervalType, TaskStateEnum } from "../../AnnualWheel.types";
import { AnnualWheelPriority } from "../../annualWheelDialog/AnnualWheelDialog.types";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations";
import ComplianceAreaWrapper from "../../../common/components/complianceAreaWrapper/ComplianceAreaWrapper";
import SubTaskCountChip from "../../../common/components/SubTaskCountChip";
import { ContractOverviewTabEnum } from "../../../contract/contractOverview/ContractOverview.types";
import { AnnualWheelActivityGeneralType } from "../../annualWheelDialog/AnnualWheelDialog.types";
import TruncatedMentionText from "@common/components/mentionInput/TruncatedMentionText";
import QuestionnaireEvaluation from "@common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation";
import { EvaluationStatus } from "@common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types";
import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";
import { NameWithColor } from "@common/entityField.types";

interface DynamicActivityCellRendererProps {
    field: ActivityFieldValue;
}

interface ContractValue {
    contractId: string;
    contractName: string;
}

interface SubTaskValue {
    subTasks: Array<string>;
    index: number;
}

interface RecurrenceValue {
    recurringInterval?: number;
    recurringIntervalType?: RecurringIntervalType;
}

function DynamicActivityCellRenderer({ field }: DynamicActivityCellRendererProps) {
    const { translateString, translateDate, translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();
    const { getContractOverviewUrl } = useUrlProvider();
    const formatRecurringInterval = useRecurringIntervalFormatting();

    function renderFieldValue() {
        if (field.value === null || field.value === undefined) {
            return "";
        }

        switch (field.type) {
            case ActivityFieldType.Text:
                return field.value;

            case ActivityFieldType.Date:
                return field.value ? translateDate(new Date(field.value)) : "";

            case ActivityFieldType.Boolean:
                return field.value ? translateString("yes") : field.value === "" ? "" : translateString("no");

            case ActivityFieldType.Status: {
                const status = field.value as TaskStateEnum;
                const statusLabel = translateString(setFirstLetterToLowerCase(TaskStateEnum[status].toString()));
                return <AnnualWheelActivityStatusChip status={status} value={statusLabel} />;
            }

            case ActivityFieldType.Priority: {
                const priority = field.value as AnnualWheelPriority;
                const priorityLabel = translateString(setFirstLetterToLowerCase(AnnualWheelPriority[priority].toString()));
                return <AnnualWheelPriorityIcon priority={priority} priorityString={priorityLabel} />;
            }

            case ActivityFieldType.Evaluation:
                return <QuestionnaireEvaluation evaluationStatus={field.value as EvaluationStatus} evaluation="" />;

            case ActivityFieldType.BusinessAreaList:
            case ActivityFieldType.TagList:
                return <ColoredChipsCell items={(field.value as NameWithColor[] | null) ?? []} />;

            case ActivityFieldType.ComplianceAreaList: {
                const areas = field.value as Array<{ id: string; name: string }>;
                if (!areas || areas.length === 0) return "";

                return (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {areas.map((c) => (
                            <ComplianceAreaWrapper key={c.id} complianceArea={c.name} />
                        ))}
                    </Box>
                );
            }

            case ActivityFieldType.SubTaskList: {
                const subTaskData = field.value as SubTaskValue;
                return <SubTaskCountChip subTasks={subTaskData.subTasks ?? []} index={subTaskData.index} />;
            }

            case ActivityFieldType.EntityList: {
                const entities = field.value as string[];
                if (!entities || entities.length === 0) {
                    if (field.fieldId === "group-companies") return translateString("allGroupEntities");
                    return "";
                }
                return entities.join(", ");
            }

            case ActivityFieldType.Contract: {
                const contract = field.value as ContractValue;
                if (!contract || !contract.contractName) return "";

                return (
                    <DotLegalLink bold to={getContractOverviewUrl(contract.contractId, ContractOverviewTabEnum.Obligation)} linkColor="primary">
                        {contract.contractName}
                    </DotLegalLink>
                );
            }

            case ActivityFieldType.RelationList: {
                const relations = field.value as string[];
                if (!relations || relations.length === 0) return "";
                return relations.join(", ");
            }

            case ActivityFieldType.Recurrence: {
                const recurrence = field.value as RecurrenceValue;
                return formatRecurringInterval(recurrence.recurringInterval, recurrence.recurringIntervalType);
            }

            case ActivityFieldType.Approval: {
                const approval = field.value as number;
                switch (approval) {
                    case ApprovalOption.ApprovalNotRequired:
                        return translateString("approvalNotRequired");
                    case ApprovalOption.ApprovalNotRequiredResponsibleGetNotified:
                        return translateString("approvalNotRequiredResponsibleGetNotified");
                    case ApprovalOption.ApprovalRequiredByResponsible:
                        return translateString("approvalRequiredByResponsible");
                    default:
                        return "";
                }
            }

            case ActivityFieldType.GeneralType:
                return translateAnnualWheelActivityGeneralTypeEnum(AnnualWheelActivityGeneralType[field.value as number].toString());

            case ActivityFieldType.Html:
                return <TruncatedMentionText value={field.value as string} />;

            default:
                return field.value?.toString() || "";
        }
    }

    const shouldLimitWidth = field.type === ActivityFieldType.Html;
    const isRightAligned = field.type === ActivityFieldType.Int;

    return (
        <TableCell sx={shouldLimitWidth ? { maxWidth: "250px", width: "250px", minWidth: "250px" } : undefined} align={isRightAligned ? "right" : "left"}>
            {renderFieldValue()}
        </TableCell>
    );
}

export default DynamicActivityCellRenderer;
